import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import _ from 'lodash';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import {
  annotatePendingChanges,
  buildPendingChangesSaveSummary,
  flattenPendingChanges,
} from 'utils/pendingChangesAuthors';

import { getUuid, selectIsClaimAllowed } from 'store/auth/selectors';
import { changePendingChangesAuthorsSelectionAction } from 'store/loanApplications/actions/pendingChangesAuthorsSelection.actions';
import { selectLoanChangesHistory } from 'store/loanApplications/selectors/loanChangesHistory.selectors';
import {
  selectPendingChangesAuthors,
  selectSelectedOtherPendingChangesAuthors,
} from 'store/loanApplications/selectors/pendingChangesAuthors.selectors';
import {
  selectIsViewOthersPendingChangesAllowed,
  selectSelectedPendingChangesAuthorKeys,
} from 'store/loanApplications/selectors/pendingChangesAuthorsSelection.selectors';

import { normalizeLoanChangesHistoryTableData } from '../../LoanInfoHeader/LoanChangesHistoryTable/LoanChangesHistoryTable.utils';

import useCustomFieldHistoryContext from './useCustomFieldHistoryContext';
import useFeeHistoryContext from './useFeeHistoryContext';

/**
 * Shared state and handlers for "pending changes by user": who is ticked, toggling people, and the
 * save summary for a set of authors. Inert (empty) when the feature flag is off.
 */
const usePendingChangesAuthorsControls = () => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const isEnabled = useFeatureToggle(FeatureFlag.PendingChangesByUser);

  const currentUserId = useSelector(getUuid);
  const allAuthors = useSelector(selectPendingChangesAuthors);
  const isViewOthersAllowed = useSelector(selectIsViewOthersPendingChangesAllowed);
  const selectedAuthorKeys = useSelector(selectSelectedPendingChangesAuthorKeys);
  const selectedOtherAuthors = useSelector(selectSelectedOtherPendingChangesAuthors);
  const loanChangesHistory = useSelector(selectLoanChangesHistory);
  const isOthersSaveAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_OTHER_USER_CHANGES),
  );

  const customFieldContext = useCustomFieldHistoryContext();
  const feeTypeContext = useFeeHistoryContext();

  const authors = useMemo(
    () =>
      isViewOthersAllowed ? allAuthors : allAuthors.filter(({ isCurrentUser }) => isCurrentUser),
    [isViewOthersAllowed, allAuthors],
  );

  const currentUserAuthor = useMemo(
    () => authors.find(({ isCurrentUser }) => isCurrentUser) ?? null,
    [authors],
  );

  const ownChangesCount = currentUserAuthor?.changesCount ?? 0;
  const hasOthersSelected = isEnabled && !!selectedOtherAuthors.length;

  const onChangeSelection = useCallback(
    authorKeys =>
      dispatch(changePendingChangesAuthorsSelectionAction({ applicationId, authorKeys })),
    [dispatch, applicationId],
  );

  const onToggleAuthor = useCallback(
    authorKey => onChangeSelection(_.xor(selectedAuthorKeys, [authorKey])),
    [onChangeSelection, selectedAuthorKeys],
  );

  const onSelectAll = useCallback(
    () => onChangeSelection(_.uniq([...selectedAuthorKeys, ...authors.map(({ key }) => key)])),
    [onChangeSelection, selectedAuthorKeys, authors],
  );

  const getSaveSummary = useCallback(
    targetAuthorKeys =>
      buildPendingChangesSaveSummary({
        changes: normalizeLoanChangesHistoryTableData(
          annotatePendingChanges(flattenPendingChanges(loanChangesHistory)),
          customFieldContext,
          feeTypeContext,
        ),
        targetKeys: targetAuthorKeys,
        authors,
        feeTypeNamesById: feeTypeContext?.feeTypeNamesById,
      }),
    [loanChangesHistory, customFieldContext, feeTypeContext, authors],
  );

  return {
    isEnabled,
    currentUserId,
    authors,
    selectedAuthorKeys,
    selectedOtherAuthors,
    ownChangesCount,
    hasOthersSelected,
    isOthersSaveAllowed,
    onToggleAuthor,
    onSelectAll,
    getSaveSummary,
  };
};

export default usePendingChangesAuthorsControls;
